import { useEffect, useRef } from 'react';

/**
 * handle scroll
 * @param callback
 * @param duration
 * @param el
 */
export const useScrollThrottle = (
  callback: (e?: HTMLElement) => void,
  duration: number = 100,
  el?: string
) => {
  const targetRef = useRef<HTMLElement | Window | null>(null);
  const throttleTimer = useRef<NodeJS.Timeout | null>(null);
  const targetDom = useRef<HTMLElement | null>(null);

  const handleScroll = () => {
    if (throttleTimer.current) return;

    if (el && !targetDom.current) {
      targetDom.current = document.querySelector(el);
    }

    throttleTimer.current = setTimeout(() => {
      targetDom.current ? callback(targetDom.current) : callback();
      throttleTimer.current = null;
    }, duration);
  };

  useEffect(() => {
    const target: any = el ? document.querySelector(el) : window;
    targetRef.current = target || window;

    if (targetRef.current) {
      targetRef.current.addEventListener('scroll', handleScroll);
    }

    return () => {
      if (targetRef.current) {
        targetRef.current.removeEventListener('scroll', handleScroll);
      }
    };
  }, [el, duration, callback]);
};

import { useState } from 'react';

/**
 * handle scroll to bottom
 * @param callback
 * @param offset
 * @param duration
 * @param el
 */
export const useScrollBottom = (
  callback: () => void,
  offset: number = 100,
  duration: number = 100,
  el?: string
) => {
  const [lock, setLock] = useState(false);

  useScrollThrottle(
    () => {
      let cHeight: number, top: number, h: number;

      if (el) {
        const target = document.querySelector(el) as HTMLElement;
        cHeight = target.clientHeight;
        top = target.scrollTop;
        h = target.scrollHeight;
      } else {
        cHeight = document.documentElement.clientHeight;
        top = document.documentElement.scrollTop;
        h = document.documentElement.scrollHeight;
      }

      const bottomCondition = top + cHeight > h - offset;

      if (bottomCondition && !lock) {
        callback();
        setLock(true);
      }

      if (!bottomCondition) {
        setLock(false);
      }
    },
    duration,
    el
  );
};

/**
 * handle scroll top
 * @param callback
 * @param offset
 * @param duration
 * @param el
 */
export const useScrollTop = (
  callback: () => void,
  offset: number = 100,
  duration: number = 100,
  el?: string
) => {
  const [lock, setLock] = useState(false);

  useScrollThrottle(
    () => {
      let cHeight: number, top: number;

      if (el) {
        const target = document.querySelector(el) as HTMLElement;
        cHeight = target.clientHeight;
        top = target.scrollTop;
      } else {
        cHeight = document.documentElement.clientHeight;
        top = document.documentElement.scrollTop;
      }

      const topCondition = top <= offset;

      if (topCondition && !lock) {
        callback();
        setLock(true);
      }

      if (!topCondition) {
        setLock(false);
      }
    },
    duration,
    el
  );
};
